import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, TrendingUp, Leaf, Droplet } from 'lucide-react';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { useGSClientMetrics } from '@/hooks/useGSClientMetrics';
import { useEnhancedSustainabilityMetrics } from '@/hooks/useEnhancedSustainabilityMetrics';
import { GSProductsList } from '@/components/gs-client/GSProductsList';
import { EnhancedMetricsOverview } from '@/components/gs-client/EnhancedMetricsOverview';
import { SustainabilityCharts } from '@/components/gs-client/SustainabilityCharts';
import { TopPerformersCard } from '@/components/gs-client/TopPerformersCard';

export default function GSClientPortal() {
  const navigate = useNavigate();
  const { totalProducts, saferIngredientsPercentage, carbonReduced, waterSaved, loading, error } = useGSClientMetrics();
  const enhancedMetrics = useEnhancedSustainabilityMetrics();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <AppNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">GS Client Portal</h1>
          <p className="text-muted-foreground mt-2">
            Monitor your product portfolio and sustainability metrics
          </p>
        </div>

        {/* Enhanced Metrics Overview */}
        <EnhancedMetricsOverview 
          overview={enhancedMetrics.overview}
          loading={enhancedMetrics.loading}
        />

        {/* Error State */}
        {(error || enhancedMetrics.error) && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <p className="text-destructive">
                Error loading data: {error || enhancedMetrics.error}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Top Performers and Products */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div className="xl:col-span-2">
            <GSProductsList />
          </div>
          <div>
            <TopPerformersCard topPerformers={enhancedMetrics.topPerformers} />
          </div>
        </div>

        {/* Analytics and Charts */}
        {!enhancedMetrics.loading && enhancedMetrics.trends.monthlyData.length > 0 && (
          <div className="mb-8">
            <SustainabilityCharts
              monthlyData={enhancedMetrics.trends.monthlyData}
              riskLevels={enhancedMetrics.categoryBreakdown.riskLevels}
              productTypes={enhancedMetrics.categoryBreakdown.productTypes}
              certificationStatus={enhancedMetrics.certificationStatus}
            />
          </div>
        )}
      </div>
    </div>
  );
}